import React, { useMemo } from 'react';
import clsx from 'clsx';
import { MessageDescriptor, useIntl } from 'react-intl';
import { StreamingPlaySourceGroup, StreamingPlaySourceRow } from 'fym-shared/src/music24/streamingSections';
import { TypedMemo } from '../../../../common/types';
import { changePctOf } from '../../streamingFormat';
import { playSourcesMessages as messages } from '../../playSourcesMessages';
import { StreamingBlock } from '../../parts/StreamingBlock';
import { RankedRow } from '../../parts/RankedRow';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import shared from '../../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the period sections; each part uses its own slice of it */
import styles from '../../StreamingPeriod.module.scss';
/* eslint-enable css-modules/no-unused-class */

const GROUP_LABELS: Record<Exclude<StreamingPlaySourceGroup, 'store'>, MessageDescriptor> = {
  you: messages.groupYou,
  listeners: messages.groupListeners,
  other: messages.groupOther,
};

interface PlaySourceRowsProps {
  rows: readonly StreamingPlaySourceRow[];
  /** The name of the store group chip: "Spotify", "Audiomack". */
  storeName: string;
  sourceName: string;
}

/**
 * "Where plays come from" (design streaming-sections b1): one row per name of the source, largest
 * first. The group chip says who brought the play.
 */
const PlaySourceRowsComponent: React.FC<PlaySourceRowsProps> = ({ rows, storeName, sourceName }) => {
  const intl = useIntl();
  const ranked = useMemo(() => {
    const sorted = [...rows].sort((left, right) => right.streams - left.streams);
    const total = sorted.reduce((sum, row) => sum + row.streams, 0);
    return sorted.map((row, index) => ({
      ...row,
      rank: index + 1,
      share: total > 0 ? row.streams / total : null,
      changePct: changePctOf(row.streams, row.previousStreams),
    }));
  }, [rows]);

  return (
    <StreamingBlock
      title={intl.formatMessage(messages.rowsTitle)}
      meta={intl.formatMessage(messages.rowsCount, { count: ranked.length })}
      testId="streaming-playSources-rows"
    >
      <ol className={shared.rows}>
        {ranked.map((row) => (
          <RankedRow
            key={row.name}
            rank={row.rank}
            name={
              <span className={styles.sourceName}>
                <span>{row.name}</span>
                <span
                  className={clsx(
                    styles.groupChip,
                    row.group === 'you' && styles.groupYou,
                    row.group === 'store' && styles.groupStore
                  )}
                >
                  {row.group === 'store' ? storeName : intl.formatMessage(GROUP_LABELS[row.group])}
                </span>
              </span>
            }
            share={row.share}
            value={row.streams}
            changePct={row.changePct}
          />
        ))}
      </ol>
      <p className={shared.footnote}>
        {sourceName} · {intl.formatMessage(messages.changeNote)}
      </p>
    </StreamingBlock>
  );
};

export const PlaySourceRows = TypedMemo(PlaySourceRowsComponent);
